import { Chip, cn } from '@patchbay/ui'
import { Clipboard, FileLock2, RotateCcw } from 'lucide-react'
import * as React from 'react'
import type { RileyAudience, RileyCitation } from './contracts'

export const PATCHBAY_CITATION_MIME = 'application/x-patchbay-citation+json'

const TYPE_LABEL: Record<string, string> = {
  desk_thread: 'Desk',
  desk_comment: 'Desk note',
  conduit_note: 'Conduit note',
  conduit_record: 'Conduit',
  kb: 'Knowledge',
  unifi: 'UniFi',
  pbx: 'PBX',
  ninja: 'Ninja',
  cambium: 'Cambium',
  zabbix: 'Zabbix',
  books_invoice: 'Books',
  triage_log: 'Triage',
  riley_writeback: 'Riley write-back',
}

export function citationTransferValue(citation: RileyCitation): string {
  return JSON.stringify(citation)
}

export function citationFromTransfer(data: Pick<DataTransfer, 'getData'>): RileyCitation | null {
  const raw = data.getData(PATCHBAY_CITATION_MIME)
  if (!raw) return null
  try {
    const citation = JSON.parse(raw) as Partial<RileyCitation>
    if (typeof citation.type !== 'string' || typeof citation.id !== 'string' || typeof citation.label !== 'string') return null
    return { ...citation, internal_only: citation.internal_only === true } as RileyCitation
  } catch {
    return null
  }
}

export function RileyCitationChip({ citation, className }: { citation: RileyCitation; className?: string }) {
  const selfWrite = citation.type === 'riley_writeback'
  const detail = citation.internal_only
    ? 'Internal only. A customer or contractor composer strips this citation and reports only the count.'
    : selfWrite
      ? "Riley's own write-back; not independent evidence."
      : `${TYPE_LABEL[citation.type] ?? 'Source'} citation`

  const writeTransfer = (data: DataTransfer) => {
    data.setData(PATCHBAY_CITATION_MIME, citationTransferValue(citation))
    data.setData('text/plain', citation.label)
  }

  return (
    <button
      type="button"
      draggable
      aria-label={`Citation: ${citation.label}`}
      title={`${citation.label}. ${detail} Copy or drag into a composer.`}
      onCopy={(event) => {
        writeTransfer(event.clipboardData)
        event.preventDefault()
      }}
      onDragStart={(event) => writeTransfer(event.dataTransfer)}
      className={cn('min-w-0 max-w-full cursor-copy rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/40', className)}
    >
      <Chip tone={selfWrite ? 'warning' : citation.internal_only ? 'internal' : 'outline'} className="max-w-full min-w-0">
        {selfWrite ? <RotateCcw aria-hidden /> : citation.internal_only ? <FileLock2 aria-hidden /> : <Clipboard aria-hidden />}
        <span className="min-w-0 truncate">{citation.label}</span>
        {selfWrite ? <span className="text-2xs">own write-back</span> : null}
        {citation.internal_only ? <span className="text-2xs">internal</span> : null}
      </Chip>
    </button>
  )
}

export function RileyCitationChips({ citations }: { citations: RileyCitation[] }) {
  if (citations.length === 0) return null
  return (
    <div className="mt-2 flex min-w-0 max-w-full flex-wrap gap-1.5">
      {citations.map((citation) => (
        <RileyCitationChip key={`${citation.type}:${citation.id}`} citation={citation} />
      ))}
    </div>
  )
}

/** Fail closed: an internal-only citation is staff-only, so every audience except `internal` scrubs it
 * (disclosure rule 10 covers customer, contractor and vendor; `unclassified` is treated as third party). */
function shouldScrub(citation: RileyCitation, audience: RileyAudience): boolean {
  // Zabbix names an internal monitoring system and the site router. Staff may keep it; every other audience loses it
  // even if a caller forgot internal_only (#1010, hard rule 1).
  const staffEvidence = citation.internal_only || citation.type === 'zabbix'
  return staffEvidence && audience !== 'internal'
}

/**
 * Wraps a composer owned by another slice. It consumes typed citation paste/drag data before the
 * editor sees it. The report deliberately contains counts only, never the stripped citation value.
 */
export function CitationPasteBoundary({
  audience,
  onCitation,
  children,
  className,
}: {
  audience: RileyAudience
  onCitation?: (citation: RileyCitation) => void
  children: React.ReactNode
  className?: string
}) {
  const [stripped, setStripped] = React.useState(0)

  const receive = (citation: RileyCitation, preventDefault: () => void) => {
    if (shouldScrub(citation, audience)) {
      preventDefault()
      setStripped((count) => count + 1)
      return
    }
    if (onCitation) {
      preventDefault()
      onCitation(citation)
    }
  }

  return (
    <div
      className={className}
      data-citation-paste-boundary
      data-audience={audience}
      onPasteCapture={(event) => {
        const citation = citationFromTransfer(event.clipboardData)
        if (citation) receive(citation, () => event.preventDefault())
      }}
      onDropCapture={(event) => {
        const citation = citationFromTransfer(event.dataTransfer)
        if (citation) receive(citation, () => event.preventDefault())
      }}
      onDragOverCapture={(event) => {
        if (event.dataTransfer.types.includes(PATCHBAY_CITATION_MIME)) event.preventDefault()
      }}
    >
      {stripped > 0 ? (
        <div className="mb-2" aria-live="polite">
          <Chip tone="warning" data-slot="scrub-report">
            <FileLock2 aria-hidden /> Scrub report: {stripped} internal-only citation{stripped === 1 ? '' : 's'} stripped
          </Chip>
        </div>
      ) : null}
      {children}
    </div>
  )
}
